import { ORDER_CONFIG } from "@/config/order";

type MinimumChannel = keyof typeof ORDER_CONFIG.minimumOrderValue;

const isKnownChannel = (channel: string): channel is MinimumChannel =>
	channel in ORDER_CONFIG.minimumOrderValue;

/**
 * Returns the minimum order value for the given channel in channel-native
 * currency. Unknown channels return 0 (= no minimum) and emit a warning so
 * the storefront never locks customers out of an unconfigured channel.
 */
export function getMinimumOrderValue(channel: string): number {
	if (!isKnownChannel(channel)) {
		console.warn(`[minimum-order] Channel "${channel}" has no MOV config; treating as no minimum.`);
		return 0;
	}
	return ORDER_CONFIG.minimumOrderValue[channel];
}

export type MinimumOrderCheck = {
	isMet: boolean;
	threshold: number;
	shortfall: number;
};

/**
 * Compares a cart subtotal (channel-native currency) against the configured
 * minimum order value for the channel. `shortfall` is 0 when the threshold
 * is met or when the channel has no configured minimum.
 */
export function checkMinimumOrder(subtotal: number, channel: string): MinimumOrderCheck {
	const threshold = getMinimumOrderValue(channel);
	if (threshold <= 0) {
		return { isMet: true, threshold: 0, shortfall: 0 };
	}
	const isMet = subtotal >= threshold;
	const shortfall = isMet ? 0 : Number((threshold - subtotal).toFixed(2));
	return { isMet, threshold, shortfall };
}
